// Ekran 03 — Laboratuvar API istemcisi.
// Ekran 02 de kullanıyor: "Numune al" drawer'ı ve numune sekmesi ortak.

import { api } from '../../lib/api'

export type NumuneTipi = 'lot' | 'fermenter' | 'urun'
export type NumuneDurumu = 'bekliyor' | 'analizde' | 'tamamlandi' | 'uygunsuz'

export type NumuneOzet = {
  id: string
  no: string
  tip: NumuneTipi
  durum: NumuneDurumu
  alinmaZamani: string
  lotId: string | null
  lotBelgeNo: string | null
  cariUnvan: string | null
  stokKod: string | null
  stokAd: string | null
  uniteId: string | null
  uniteKod: string | null
  uniteAd: string | null
  analizSayisi: number
  /** Kaydı açan kullanıcı — §5.1'de ayrı sütun yok, sahipten çözülür. */
  alanKisi: string | null
  /** Tabloda "Son Değer" sütunu; tipe göre farklı ölçüm. */
  sonDeger: SonDeger | null
}

/**
 * "Son Değer" hücresinin parçaları. Sayı biçimlenmemiş gelir (§12) — hücre
 * referanstaki gibi `<b class="mono">` ile basar; `bant` yalnız fermenterde
 * dolar ve eşik dışı ölçümü renkle ayırır (ref:456).
 */
export type SonDeger = {
  etiket: string
  deger: number | null
  basamak: number
  birim: string | null
  bant: Bant | null
}

/** §6.3 renk bandı — sunucudaki `Bant` enum'ının aynısı. */
export type Bant = 'saglikli' | 'izle' | 'kritik'

/**
 * Ham `ayar_esik` satırı. `kalip` hangi kuralın uygulanacağını söyler:
 * `ustSinir` → değer büyüdükçe kötüleşir, `aralik` → iki taraflı (pH).
 */
export type EsikDto = {
  gosterge: string
  normalMin: number | null
  normalMax: number | null
  amber: number | null
  kirmizi: number | null
  kalip: 'ustSinir' | 'aralik'
}

/** Bir ölçümün §6.3 bandı — rozet rengi bundan gelir. */
export type BantDto = {
  alan: string
  deger: number | null
  bant: Bant
  etiket: string
}

/** "Cari Bazında KM Ort." satırı — numune sekmesinin sağ sütunu. */
export type CariKmKirilimi = {
  cariUnvan: string
  analizSayisi: number
  ortalama: number | null
}

export type KmKiyas = {
  numuneOrt: number | null
  stokKarti: number | null
  /** Kartın oKM literatürü; KPI şeridi Kül literatürünü 100 − bu değerden türetir. */
  stokKartiOkm: number | null
  substratGeneli: number | null
  cariUnvan: string | null
  cariOrt: number | null
  cariKirilimi: CariKmKirilimi[]
}

export type TrendSatiri = {
  zaman: string
  ph: number | null
  fosTac: number | null
  bant: 'saglikli' | 'izle' | 'kritik'
}

export type NumuneDetay = {
  numune: NumuneOzet
  analizler: AnalizDto[]
  bantlar: BantDto[]
  kmKiyas: KmKiyas | null
  fosTacTrendi: TrendSatiri[]
}

export type AnalizDto = {
  id: string
  numuneId: string
  /** Ekran 03'teki "#N" etiketi — numune başına artar, revizyondan bağımsız. */
  siraNo: number
  km: number | null
  okm: number | null
  kul: number | null
  ph: number | null
  fosTac: number | null
  nh4: number | null
  vfa: number | null
  iletkenlik: number | null
  nem: number | null
  n: number | null
  p: number | null
  k: number | null
  revizyonNo: number
  revizyonGerekce: string | null
  /** oKM + Kül − 100; ekranın canlı engeli bunu gösteriyor. */
  kulSapmasi: number | null
  /** Kaydın açıldığı an — tablo "Tarih" sütunu ve "Son Analizler" `.dt`. */
  zaman: string
  /** LOT tablosundaki "Analist" sütunu — kayıt sahibinden çözülür. */
  analistAd: string | null
}

export type LabOzeti = {
  bekleyen: number
  analizde: number
  bugunTamamlanan: number
  uygunsuz: number
  bugunAnaliz: number
  buAyAnaliz: number
  esikDisiUyari: number
  fosTacSon: number | null
  fosTacBant: Bant | null
  fosTacUnite: string | null
  /**
   * Fermenter formunun canlı rozetlerinin okuduğu §6.3 satırları. Ayarlar ucu
   * laboranta kapalı olduğu için eşikler lab özetiyle birlikte geliyor.
   */
  esikler: EsikDto[]
}

export type AnalizSonucu = {
  analiz: AnalizDto
  havuzEtkinKm: number | null
  havuzEtkinOkm: number | null
  havuzAnalizSayisi: number
  mesaj: string
}

export type AnalizAlanlari = {
  km?: number | null
  okm?: number | null
  kul?: number | null
  ph?: number | null
  fosTac?: number | null
  nh4?: number | null
  vfa?: number | null
  iletkenlik?: number | null
  nem?: number | null
  n?: number | null
  p?: number | null
  k?: number | null
}

export const numuneler = {
  liste: (s: { durum?: NumuneDurumu; tip?: NumuneTipi } = {}) => {
    const p = new URLSearchParams()
    if (s.durum) p.set('durum', s.durum)
    if (s.tip) p.set('tip', s.tip)
    const qs = p.toString()
    return api<NumuneOzet[]>(`/api/numuneler${qs ? `?${qs}` : ''}`)
  },

  ozet: () => api<LabOzeti>('/api/numuneler/ozet'),

  detay: (id: string) => api<NumuneDetay>(`/api/numuneler/${id}`),

  sonrakiNo: () => api<{ no: string }>('/api/numuneler/sonraki-no'),

  olustur: (govde: { tip: NumuneTipi; lotId?: string | null; uniteId?: string | null }) =>
    api<NumuneOzet>('/api/numuneler', { yontem: 'POST', govde }),
}

export const analizler = {
  liste: (numuneId: string) => api<AnalizDto[]>(`/api/analizler?numuneId=${numuneId}`),

  ekle: (numuneId: string, alanlar: AnalizAlanlari) =>
    api<AnalizSonucu>('/api/analizler', { yontem: 'POST', govde: { numuneId, ...alanlar } }),

  /** K15: düzeltme = revizyon; gerekçe zorunlu, revizyonNo artar. */
  revize: (id: string, alanlar: AnalizAlanlari, revizyonGerekce: string) =>
    api<AnalizSonucu>(`/api/analizler/${id}`, {
      yontem: 'PUT',
      govde: { ...alanlar, revizyonGerekce },
    }),
}

/** Ekran 03'ün canlı engeli — sunucudaki AnalizKurallari ile aynı kural. */
export const KUL_TOLERANS = 3

export function kulSapmasi(okm: number | null, kul: number | null): number | null {
  if (okm === null || kul === null) return null
  return okm + kul - 100
}

export function kulGecerliMi(okm: number | null, kul: number | null): boolean {
  const s = kulSapmasi(okm, kul)
  return s === null || Math.abs(s) <= KUL_TOLERANS
}
